import { supabase } from './supabase-config';
import invariant from 'tiny-invariant';

import { breadcrumb, log } from '@utils/logging';
import { getSignedInUser } from './supabase-utils';
import { STATUS, Team, TEAM_ROLE, TeamAthlete, UserTeam } from '@type/db-types';
import { TeamForm } from '@components/teamCreation/CreateTeamUtils';
import { createUserTeamMembership } from './users';

const TEAMS = 'teams';
const TEAM_MEMBERSHIP = 'user_team';

export const getTeams = async (): Promise<TeamAthlete[]> => {
    const user = await getSignedInUser();
    invariant(user, 'User must be signed in to get teams');

    const { data, error } = await supabase.from('TeamAthletes').select('*').eq(`userId`, user.id);

    if (error) {
        log(`[getTeams] ${error.message}`, error);
        return [];
    }

    breadcrumb('getTeams', { data });
    return data as TeamAthlete[];
};

export async function updateTeam(
    team: Partial<TeamForm>,
    description = 'updateTeam',
): Promise<Team | null> {
    breadcrumb('Team', { team, description });

    if (team.id) {
        const { data, error } = await supabase
            .from(TEAMS)
            .update(team)
            .eq('id', team.id)
            .select('*')
            .single();

        if (error || !data) {
            console.warn(error);
            return null;
        } else {
            console.log(`Team '${team.name}' updated`, data);
        }

        return data as Team;
    } else {
        log(`[updateTeam] Team information missing`, team);
        return null;
    }
}

export async function createTeam(team: TeamForm, description: string): Promise<Team | null> {
    breadcrumb('createTeam', { description, team });
    const user = await getSignedInUser();
    invariant(user, 'User must be signed in to get teams');
    // @ts-expect-error - remove form fields
    delete team.stripeDetailsSubmitted;

    const { data, error } = await supabase.from(TEAMS).insert(team).select('*').single();

    if (error) {
        log(`[createTeam] ${error.message}`, error);
    } else {
        console.log(`team created `, data);

        // update user team relationship
        await createUserTeamMembership({
            paymentId: null,
            teamId: data.id,
            role: TEAM_ROLE.OWNER,
            status: STATUS.ACTIVE,
            userId: team.createdByUserId || user.id,
        });
    }

    return data as Team;
}

export async function updateTeamStatus(status: STATUS, membershipId: string) {
    const { data, error } = await supabase
        .from(TEAM_MEMBERSHIP)
        .update({ status })
        .eq('id', membershipId)
        .select('*')
        .single();

    if (error) {
        log(`[updateTeamStatus] ${error.message}`, error);
    }

    breadcrumb('updateTeamStatus', { data, error });

    return data || null;
}

// update team role
export const updateTeamRole = async (role: TEAM_ROLE, membershipId: string) => {
    const { data, error } = await supabase
        .from(TEAM_MEMBERSHIP)
        .update({ role })
        .eq('id', membershipId)
        .select('*')
        .single();

    if (error) {
        log(`[updateTeamRole] ${error.message}`, error);
    }

    breadcrumb('updateTeamRole', { data, error });

    return data || null;
};

export async function getTeamStripeId(
    teamId: string,
): Promise<{ stripeId: string; userId: string }> {
    const { data, error } = await supabase
        .rpc('getTeamStripeId', {
            teamId,
        })
        .single();

    breadcrumb('getTeamStripeId', { data, error });

    if (error || !data) {
        log(`[getTeamStripeId]: ${error?.message || 'team stripe account not found'}`, {
            data,
            error,
        });
        throw new Error(`[getTeamStripeId]: ${error?.message || 'team stripe account not found'}`);
    }

    return data;
}

// get team membership status
export const getTeamMembershipInfo = async (
    teamId: string,
    userId: string,
    expectData: boolean = false,
): Promise<UserTeam | null> => {
    const { data, error } = await supabase
        .from(TEAM_MEMBERSHIP)
        .select('*')
        .eq('teamId', teamId)
        .eq('userId', userId)
        .single();

    if (error && expectData) {
        log(`[getTeamMembershipInfo] ${error.message}`, error);
    }

    breadcrumb('getTeamMembershipInfo', { data, error });

    return (data as UserTeam) || null;
};
